// THE ROUTE GUARD, AND IT IS A SERVER DECISION.
//
// The prototype's guards were a client-side high-water mark, so typing a URL walked past them: Ohio
// plus `#/checkout` rendered "One appeal — $29.99", and the Nebraska no-case plus `#/letter` rendered
// "Ready to file." (WEB_PATHWAY_INVENTORY.md §4 "Worse" #1, all three walked).
//
// Here a paid screen cannot render until it has ASKED. `PaidRoute` runs the screen's own call — the one
// it needs its data from anyway — and hands the response down only when three things are true:
//
//   1. the call came back `ok`. A refusal is rendered as the server's sentence, which is what a deep
//      link to a paid route without a spend produces: 403 NOT_ENTITLED with its own copy;
//   2. `appeal` is present. A response that carries no snapshot cannot say the owner is entitled, and
//      SILENCE NEVER DISARMS A GUARD — this is a charge-adjacent gate, so it fails closed;
//   3. C-1 RAN and did not block, and `entitlement.unlocked` is true. Both, because a decision that
//      never ran is not a pass.
//
// There is no other way into a step: the children are a function of the response, so there is nothing
// to render before the answer arrives.

//
// THE GUARD PAGES SPEAK AS THE APP DOES (WEB-PARITY-1B, SW-8, SW-10). They used to say "Checking with the server…",
// "Entitlement hasn't been decided yet — the answer carried no appeal" and "Entitlement hasn't been decided yet, so this
// step stays closed". Now: an answer that carried no appeal is one this page could not read, so it says the approved
// unreachable sentence (item 7); an appeal with no credit on it says the app's own LetterService.swift:227; and a C-1 that
// never ran is a check that has not run, so it says W-2. The loading line is the step's own, from the app.

import type { ReactNode } from "react";
import type { AppealSnapshot } from "@contract";
import { useReportAppeal } from "../app/railAppeal";
import { useCall } from "../app/useCall";
import type { AnyDecision } from "../transport/types";
import type { V2Failure, V2Result } from "@contract";
import { HeldLine } from "./copy";
import type { AppWordingId } from "./heldCopy";
import { Refusal } from "./Refusal";

export interface PaidResponse {
  appeal: AppealSnapshot | null;
  decisions: { [key: string]: unknown };
  degraded: readonly { field: string; problem: string }[];
}

export function PaidRoute<T extends PaidResponse>({
  probe,
  deps,
  pending = "loading.appeal",
  pendingView,
  failed,
  children,
}: {
  probe: () => Promise<V2Result<T>>;
  deps: readonly unknown[];
  /** The app's own line for what this step is waiting on. Default: "Reviewing your appeal…". */
  pending?: Extract<AppWordingId, `loading.${string}`>;
  /** A step's own designed wait, in place of that one line (`/comps`, 2026-10-14; no step uses it since 2026-10-21, when
   *  `/comps` took `pending` for its read and moved the designed wait to its automatic search). It carries its own status
   *  region. It is shown only while the probe is out: nothing in it can reach the step before the server has answered. */
  pendingView?: ReactNode;
  /** A step's own answer to a failure it has words for (WEB-PARITY-2A: `/details`' "We couldn't reach the records
   *  service"), or undefined for the general refusal. */
  failed?: (failure: V2Failure) => ReactNode | undefined;
  children: (response: T, appeal: AppealSnapshot) => ReactNode;
}) {
  const state = useCall(probe, deps);
  // The snapshot this step's own call answered with, handed to the rail beside it (WALK-FIX-1, ../app/railAppeal.ts).
  useReportAppeal(state.status === "ok" ? state.response.appeal : null);

  if (state.status === "pending") {
    if (pendingView !== undefined) return <>{pendingView}</>;
    return (
      <div className="focus" role="status">
        <HeldLine id={pending} className="spinner-line" />
      </div>
    );
  }
  if (state.status === "failed") {
    const own = failed?.(state.failure);
    if (own !== undefined) return <>{own}</>;
    return <Refusal failure={state.failure} context="This step" />;
  }

  const appeal = state.response.appeal;
  if (appeal === null) {
    return (
      <div className="refusal" role="alert" data-guard="no-snapshot">
        <p className="code">This step</p>
        <h1>We can't tell where this appeal stands.</h1>
        <div className="notrun" data-not-run="missingInput">
          <HeldLine id="transport.unreachable" />
        </div>
      </div>
    );
  }

  // C-1 IS CHECKED WHERE IT EXISTS, and `entitlement.unlocked` always. Not every endpoint carries C-1
  // — `v2ResolveProperty` has no entitlement decision and refuses the record phase with 403 instead —
  // so the snapshot's own answer is the floor, and C-1 narrows it further where the contract has it.
  // A C-1 that never RAN is not a pass: `!c1.ran` closes the step exactly as a block does.
  const c1 = state.response.decisions["C-1"] as AnyDecision | undefined;
  const c1Blocks = c1 !== undefined && (!c1.ran || c1.outcome === "block");
  if (c1Blocks || !appeal.entitlement.unlocked) {
    return (
      <div className="refusal" role="alert" data-guard="not-entitled">
        <p className="code">This step</p>
        <h1>This part isn't unlocked yet.</h1>
        {c1 !== undefined && c1.ran && c1.copy !== null ? (
          <div className="note stop" data-copy-code={c1.copy.code}>
            {c1.copy.text}
          </div>
        ) : (
          <div className="notrun" data-not-run={c1 !== undefined && !c1.ran ? c1.why : "notYetComputed"}>
            <HeldLine id={c1 !== undefined && !c1.ran ? "check.notRunYet" : "guard.noCredit"} />
          </div>
        )}
      </div>
    );
  }

  return <>{children(state.response, appeal)}</>;
}
